import React, { useCallback, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import {
  TASK_STATUSES,
  TASK_STATUS_LABELS,
  TaskStatus,
} from '../../lib/config/project-config'

const STATUS_PICKER_STORAGE_KEY = 'devtracker-status-picker-selected-statuses'
export const UNKNOWN_STATUS_FILTER = 'unknown' as const

const STATUS_GROUPS: { label: string; statuses: TaskStatus[] }[] = [
  { label: 'Approval', statuses: ['pending_quote', 'pending_finance_approval'] },
  { label: 'In Development', statuses: ['in_dev_progress'] },
  { label: 'Testing', statuses: ['in_dev_testing', 'in_uat_testing'] },
  { label: 'Release', statuses: ['ready_for_uat', 'ready_for_prod'] },
]

const STATUS_GROUPS_WITH_OTHER: { label: string; statuses: StatusFilter[] }[] = [
  ...STATUS_GROUPS,
  {
    label: 'Other',
    statuses: ['on_hold', 'completed', UNKNOWN_STATUS_FILTER],
  },
]

const KNOWN_STATUS_FILTER_STATUSES: TaskStatus[] = [
  ...STATUS_GROUPS.flatMap((group) => group.statuses),
  'on_hold',
  'completed',
]

export type StatusFilter = TaskStatus | typeof UNKNOWN_STATUS_FILTER

export const isStatusFilterSelected = (
  status: string,
  selectedStatusSet: ReadonlySet<StatusFilter>
) => selectedStatusSet.has(status as TaskStatus) || (
  selectedStatusSet.has(UNKNOWN_STATUS_FILTER) && !KNOWN_STATUS_FILTER_STATUSES.includes(status as TaskStatus)
)

const getDefaultSelectedStatuses = (): StatusFilter[] => [
  ...KNOWN_STATUS_FILTER_STATUSES,
  UNKNOWN_STATUS_FILTER,
]

const sanitizeSelectedStatuses = (value: unknown): StatusFilter[] => {
  if (!Array.isArray(value)) {
    return getDefaultSelectedStatuses()
  }

  const selectedStrings = value.filter((item): item is string => typeof item === 'string')
  const selectedSet = new Set(selectedStrings)
  const selectedKnownStatuses = KNOWN_STATUS_FILTER_STATUSES.filter((status) => selectedSet.has(status))
  const hasUnknownStatuses = selectedStrings.some((status) =>
    status === UNKNOWN_STATUS_FILTER ||
    !(TASK_STATUSES as readonly string[]).includes(status) ||
    !KNOWN_STATUS_FILTER_STATUSES.includes(status as TaskStatus)
  )

  if (selectedKnownStatuses.length === 0 && !hasUnknownStatuses) {
    return getDefaultSelectedStatuses()
  }

  return [
    ...selectedKnownStatuses,
    ...(hasUnknownStatuses ? [UNKNOWN_STATUS_FILTER] : []),
  ]
}

const getInitialSelectedStatuses = (): StatusFilter[] => {
  if (typeof window === 'undefined') {
    return getDefaultSelectedStatuses()
  }

  const storedValue = window.localStorage.getItem(STATUS_PICKER_STORAGE_KEY)
  if (!storedValue) {
    return getDefaultSelectedStatuses()
  }

  try {
    return sanitizeSelectedStatuses(JSON.parse(storedValue))
  } catch {
    return getDefaultSelectedStatuses()
  }
}

export const useStatusPickerSelection = () => {
  const [selectedStatuses, setSelectedStatuses] = useState<StatusFilter[]>(() => getInitialSelectedStatuses())

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    window.localStorage.setItem(STATUS_PICKER_STORAGE_KEY, JSON.stringify(selectedStatuses))
  }, [selectedStatuses])

  const selectedStatusSet = useMemo(() => new Set(selectedStatuses), [selectedStatuses])

  const toggleStatus = useCallback((status: StatusFilter) => {
    setSelectedStatuses((current) => {
      const next = new Set(current)

      if (next.has(status)) {
        next.delete(status)
      } else {
        next.add(status)
      }

      if (next.size === 0) {
        return []
      }

      return [
        ...KNOWN_STATUS_FILTER_STATUSES.filter((item) => next.has(item)),
        ...(next.has(UNKNOWN_STATUS_FILTER) ? [UNKNOWN_STATUS_FILTER] : []),
      ]
    })
  }, [])

  return {
    selectedStatuses,
    selectedStatusSet,
    toggleStatus,
  }
}

type StatusPickerProps = {
  selectedStatuses: StatusFilter[]
  onToggleStatus: (status: StatusFilter) => void
  className?: string
}

export const StatusPicker: React.FC<StatusPickerProps> = ({
  selectedStatuses,
  onToggleStatus,
  className,
}) => {
  const selectedStatusSet = useMemo(() => new Set(selectedStatuses), [selectedStatuses])

  const toggleStatuses = (statuses: StatusFilter[]) => {
    const allSelected = statuses.every((status) => selectedStatusSet.has(status))
    const nextSelected = new Set(selectedStatuses)

    statuses.forEach((status) => {
      if (allSelected) {
        nextSelected.delete(status)
      } else {
        nextSelected.add(status)
      }
    })

    statuses.forEach((status) => {
      const wasSelected = selectedStatusSet.has(status)
      const isSelected = nextSelected.has(status)

      if (wasSelected !== isSelected) {
        onToggleStatus(status)
      }
    })
  }

  return (
    <div className={clsx('flex min-w-0 flex-wrap items-start gap-x-4 gap-y-3', className)}>
      <span className="text-sm font-medium text-gray-700">Statuses</span>
      <div className="flex min-w-0 flex-1 flex-wrap gap-x-4 gap-y-3">
        {STATUS_GROUPS_WITH_OTHER.map((group) => {
          const allSelected = group.statuses.every((status) => selectedStatusSet.has(status))

          return (
            <fieldset key={group.label} className="min-w-[12rem] space-y-1.5">
              <legend className="text-xs font-semibold uppercase tracking-wide text-gray-500">{group.label}</legend>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => toggleStatuses(group.statuses)}
                  aria-pressed={allSelected}
                  className={clsx(
                    'inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold transition',
                    allSelected
                      ? 'border-slate-500 bg-slate-100 text-slate-700'
                      : 'border-dashed border-stone-400 bg-white text-slate-700 hover:bg-stone-50'
                  )}
                >
                  <span className="text-[10px]">{allSelected ? '✓' : ''}</span>
                  <span>Select all</span>
                </button>
                {group.statuses.map((status) => {
                  const isSelected = selectedStatusSet.has(status)

                  return (
                    <button
                      key={status}
                      type="button"
                      onClick={() => onToggleStatus(status)}
                      aria-pressed={isSelected}
                      className={clsx(
                        'inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition',
                        isSelected
                          ? 'border-slate-700 bg-slate-700 text-stone-100'
                          : 'border-stone-400 bg-white text-slate-700 hover:bg-stone-50'
                      )}
                    >
                      <span className="text-[10px]">{isSelected ? '✓' : ''}</span>
                      <span>{status === UNKNOWN_STATUS_FILTER ? 'Unknown' : TASK_STATUS_LABELS[status]}</span>
                    </button>
                  )
                })}
              </div>
            </fieldset>
          )
        })}
      </div>
    </div>
  )
}
